import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Mail, Phone, MapPin, Clock, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { useTranslation } from "react-i18next";

// Fix default icon (Leaflet ships without valid default markers in webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function ContactPage() {
    const { content } = useSiteContent();
    const { t } = useTranslation();
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => { document.title = `Contact — ${content.brand_name}`; }, [content.brand_name]);

    const lat = content.map_lat;
    const lng = content.map_lng;
    const zoom = content.map_zoom || 15;
    const hasMap = typeof lat === "number" && typeof lng === "number";

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await api.post("/feedback", {
                name: form.name,
                email: form.email,
                phone: form.phone,
                comment: form.message,
            });
            setSent(true);
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (e2) {
            setErr(e2.response?.data?.detail || "Could not send");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white min-h-[calc(100vh-120px)]" data-testid="contact-page">
            <section className="border-b border-slate-200 bg-slate-50">
                <div className="max-w-[1440px] mx-auto px-4 py-12">
                    <div className="label-kicker mb-3">{t("contact.kicker")}</div>
                    <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight">
                        {t("contact.title")}
                    </h1>
                    <p className="mt-4 text-slate-600 max-w-2xl">
                        {t("contact.subtitle")}
                    </p>
                </div>
            </section>

            <section className="max-w-[1440px] mx-auto px-4 py-12 grid lg:grid-cols-[1fr_1.4fr] gap-10">
                {/* Info */}
                <div className="space-y-6">
                    <div className="border border-slate-200 p-5">
                        <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-[hsl(var(--primary))] mt-1" />
                            <div className="flex-1">
                                <div className="label-kicker text-slate-500 mb-1">{t("contact.address")}</div>
                                <div className="text-sm text-slate-800" data-testid="contact-address">
                                    {content.contact_address || <span className="text-slate-400 italic">—</span>}
                                </div>
                                {content.contact_city && (
                                    <div className="text-sm text-slate-600" data-testid="contact-city">{content.contact_city}</div>
                                )}
                                {hasMap && (
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 mt-2 text-xs font-mono text-[hsl(var(--primary))] hover:underline"
                                        data-testid="contact-open-maps"
                                    >
                                        Open in Google Maps →
                                    </a>
                                )}
                            </div>
                            {hasMap && (
                                <div className="hidden sm:flex flex-col items-center gap-1 shrink-0" data-testid="contact-location-qr">
                                    <div className="bg-white p-1.5 border-2 border-slate-900">
                                        <QRCodeSVG
                                            value={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                                            size={84}
                                            level="M"
                                            includeMargin={false}
                                        />
                                    </div>
                                    <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-slate-500">
                                        <QrCode className="w-3 h-3" /> {t("contact.scan_for_directions")}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {content.contact_phone && (
                        <div className="border border-slate-200 p-5">
                            <div className="flex items-start gap-3">
                                <Phone className="w-5 h-5 text-[hsl(var(--primary))] mt-1" />
                                <div>
                                    <div className="label-kicker text-slate-500 mb-1">{t("contact.phone")}</div>
                                    <a href={`tel:${content.contact_phone}`} className="text-sm text-slate-800 font-mono" data-testid="contact-phone">
                                        {content.contact_phone}
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}

                    {content.contact_email && (
                        <div className="border border-slate-200 p-5">
                            <div className="flex items-start gap-3">
                                <Mail className="w-5 h-5 text-[hsl(var(--primary))] mt-1" />
                                <div>
                                    <div className="label-kicker text-slate-500 mb-1">{t("contact.email")}</div>
                                    <a href={`mailto:${content.contact_email}`} className="text-sm text-slate-800 font-mono" data-testid="contact-email">
                                        {content.contact_email}
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}

                    {content.contact_hours && (
                        <div className="border border-slate-200 p-5">
                            <div className="flex items-start gap-3">
                                <Clock className="w-5 h-5 text-[hsl(var(--primary))] mt-1" />
                                <div>
                                    <div className="label-kicker text-slate-500 mb-1">{t("contact.hours")}</div>
                                    <div className="text-sm text-slate-800 whitespace-pre-line" data-testid="contact-hours">{content.contact_hours}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contact form */}
                    <form onSubmit={onSubmit} className="border border-slate-200 p-5 space-y-3" data-testid="contact-form">
                        <div className="label-kicker text-slate-500">{t("contact.send_message")}</div>
                        <div>
                            <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">{t("contact.name_label")}</label>
                            <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("contact.name_placeholder")} className="input-field" data-testid="contact-form-name" />
                        </div>
                        <div>
                            <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">{t("contact.email_label")}</label>
                            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("contact.email_placeholder")} className="input-field" data-testid="contact-form-email" />
                        </div>
                        <div>
                            <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">{t("contact.phone_label")}</label>
                            <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("contact.phone_placeholder")} className="input-field" data-testid="contact-form-phone" />
                        </div>
                        <div>
                            <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">{t("contact.help_label")}</label>
                            <textarea required rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("contact.help_placeholder")} className="input-field" data-testid="contact-form-message" />
                        </div>
                        {err && <div className="text-xs text-red-600 font-mono">{err}</div>}
                        {sent && <div className="text-xs text-emerald-700 font-mono" data-testid="contact-form-success">✓ {t("contact.thanks")}</div>}
                        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="contact-form-submit">
                            {loading ? t("contact.sending") : t("contact.send")}
                        </button>
                    </form>
                </div>

                {/* Map — sticky on scroll */}
                <div className="lg:sticky lg:top-24 self-start">
                    <div className="h-[600px] bg-slate-100 border border-slate-200 relative" data-testid="contact-map-container">
                    {hasMap ? (
                        <MapContainer center={[lat, lng]} zoom={zoom} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }} data-testid="contact-map">
                            <TileLayer
                                attribution='&copy; <a href="https://osm.org">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={[lat, lng]}>
                                <Popup>
                                    <strong>{content.brand_name}</strong>
                                    <br />
                                    {content.contact_address}
                                    <br />
                                    {content.contact_city}
                                </Popup>
                            </Marker>
                        </MapContainer>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-center p-8">
                            <div>
                                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                <p className="font-mono text-sm text-slate-500">
                                    Map location not yet set.
                                </p>
                                <p className="font-mono text-xs text-slate-400 mt-2">
                                    Admin → Content → Contact &amp; location
                                </p>
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </section>
        </div>
    );
}
